import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Save, Shield, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const STATUS_META = {
  confirmed: { label: "Confirmed", tone: "bg-forest text-bone", level: 1 },
  approved_public: { label: "Approved Public", tone: "bg-forest text-bone", level: 1 },
  historical: { label: "Historical", tone: "bg-bone text-ink border border-line", level: 1 },
  internal_planning: { label: "Internal Planning", tone: "bg-terracotta/20 text-terracotta border border-terracotta/40", level: 4 },
  pending_verification: { label: "Pending Verification", tone: "bg-terracotta/20 text-terracotta border border-terracotta/40", level: 4 },
  speculative: { label: "Speculative — excluded from grants", tone: "bg-line text-ink-soft", level: 5 },
  prohibited: { label: "Prohibited — never used", tone: "bg-line text-ink-soft", level: 5 },
};

const HELP_TEXT = {
  mission: "One sentence. What the institution exists to do.",
  vision: "Where the institution is going in 5–10 years.",
  history: "Founding, milestones, key moments. Chronological.",
  programs: "Named programs currently running or in foundational population.",
  departments: "Healing · Education · Ecology · Culture · Community. Add sub-departments.",
  fiscal_sponsor: "Legal fiscal sponsor + EIN + which activities are sponsored. Verbatim language grant reviewers see.",
  leadership: "Named leaders + their roles. Founder, board, key advisors.",
  budgets: "Current operating budget, program budgets, capital budgets. High-level.",
  statistics: "Land size, program participants, volunteer counts, funding raised, verified numbers only.",
  core_narratives: "The 2–3 institutional stories used across grants, media, and public communication.",
  boilerplate: "The paragraph inserted verbatim at the top of every proposal.",
  standard_organization_summary: "One page. Read aloud without cringe. The pitch.",
  geographic_focus: "Where the work happens. Colombia, US ties, international scope.",
  communities_served: "Named communities. Indigenous partners, artists, students, healers, volunteers.",
  strategic_partners: "Confirmed partnerships. Universities, foundations, sister organizations.",
  current_assets: "Land, buildings, vehicles, equipment, digital infrastructure, intellectual property.",
  existing_facilities: "Named buildings. Kitchen, library, ceremonial spaces, volunteer housing.",
  annual_priorities: "This year's top 3–5 institutional priorities. Refreshed annually.",
};

export default function InstitutionalProfile() {
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({});
  const [verifications, setVerifications] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/institutional-profile/schema"),
        api.get("/institutional-profile"),
      ]);
      setSchema(s.data);
      const nf = {};
      s.data.fields.forEach(f => { nf[f.slug] = p.data[f.slug] || ""; });
      setForm(nf);
      setVerifications(p.data.verifications || {});
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/institutional-profile", { ...form, verifications });
      toast.success("Institutional Profile saved. Every AI Staff will use it on the next execution.");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!schema) return <div className="text-sm text-ink-soft p-6">Loading…</div>;

  const readiness = schema.fields.filter(f => (form[f.slug] || "").trim().length > 40).length;
  const total = schema.fields.length;
  const readinessPct = Math.round((readiness / total) * 100);

  return (
    <div className="space-y-6" data-testid="institutional-profile-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Shared Institutional Infrastructure · Directive #002 Rule 3</div>
          <h1 className="font-serif text-4xl mt-1 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-terracotta" strokeWidth={1.5} />
            Institutional Profile
          </h1>
          <p className="text-sm text-ink-soft mt-2 max-w-2xl leading-relaxed">
            One canonical master record. Every AI Staff member reads from this before analysis or drafting. Fields tagged <em>speculative</em> or <em>prohibited</em> are never emitted into any AI prompt. Verification tags map to Doctrine #001 Rule 6 Evidence Hierarchy.
          </p>
        </div>
        <div className="text-right">
          <div className={`font-serif text-4xl ${readinessPct >= 60 ? "text-forest" : readinessPct >= 30 ? "text-terracotta" : "text-ink-soft"}`}>{readinessPct}%</div>
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">{readiness}/{total} fields populated</div>
        </div>
      </div>

      <div className="sticky top-0 z-10 bg-bone/95 backdrop-blur border-b border-line py-3 flex items-center justify-between">
        <div className="text-xs text-ink-soft italic">
          Bust cache on save — the next AI call will use the new values.
        </div>
        <Button onClick={save} disabled={saving || !dirty} data-testid="save-profile" className="bg-forest text-bone hover:bg-ink rounded-full">
          <Save className="h-4 w-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="space-y-4">
        {schema.fields.map(f => {
          const status = verifications[f.slug] || "pending_verification";
          const meta = STATUS_META[status] || STATUS_META.pending_verification;
          const value = form[f.slug] || "";
          const emitted = !["speculative", "prohibited"].includes(status);
          return (
            <div key={f.slug} className="border border-line bg-admin-surface rounded-xl p-5" data-testid={`profile-field-${f.slug}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-xl text-ink">{f.label}</h3>
                    <Badge className={`text-[10px] uppercase tracking-widest ${meta.tone}`}>
                      {emitted ? <CheckCircle2 className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> : <AlertTriangle className="h-3 w-3 inline mr-1" strokeWidth={1.5} />}
                      Level {meta.level} · {meta.label}
                    </Badge>
                    {!emitted && (
                      <Badge className="bg-line text-ink-soft text-[10px]">
                        <Shield className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> Not sent to AI
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-ink-soft mt-1">{HELP_TEXT[f.slug]}</div>
                </div>
                <select
                  value={status}
                  onChange={e => { setVerifications({ ...verifications, [f.slug]: e.target.value }); setDirty(true); }}
                  data-testid={`status-${f.slug}`}
                  className="h-8 text-xs border border-line rounded-md bg-bone px-2"
                >
                  {schema.verification_statuses.map(s => (
                    <option key={s} value={s}>Level {STATUS_META[s]?.level} · {STATUS_META[s]?.label}</option>
                  ))}
                </select>
              </div>
              <Textarea
                rows={4}
                value={value}
                onChange={e => { setForm({ ...form, [f.slug]: e.target.value }); setDirty(true); }}
                data-testid={`value-${f.slug}`}
                placeholder={HELP_TEXT[f.slug]}
                className="text-sm"
              />
              <div className="text-[10px] text-ink-soft mt-1 text-right">{value.length} chars</div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-ink-soft italic border-t border-line pt-4">
        Doctrine #002 Rule 3: One canonical institutional profile. Every AI reads from the same source. Never duplicate institutional facts.
      </div>
    </div>
  );
}
